import React, { useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const RegisterRestaurantModal = ({ onRegister, onClose }) => {
    const [restaurant, setRestaurant] = useState({
        logo: '',
        name: '',
        owner: '',
        GST: '',
        phone: '',
        email: '',
        password: '',
        taxPercentage: '', // Store as string to preserve exact decimal values
    });
    const [error, setError] = useState(null);
    console.log('Current taxPercentage type:', restaurant);
    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        
        // For taxPercentage, store as string to preserve exact decimal values
        if (name === 'taxPercentage') {
            // Validate that it's a valid number format
            const isValidNumber = /^-?\d*\.?\d*$/.test(value);
            if (isValidNumber || value === '') {
                processedValue = value; // Store as string
            } else {
                return; // Invalid input, don't update
            }
        }
        
        setRestaurant({ ...restaurant, [name]: processedValue });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Convert taxPercentage from string to number before saving
            const dataToSave = {
                ...restaurant,
                taxPercentage: restaurant.taxPercentage !== '' && restaurant.taxPercentage != null
                    ? Number(String(restaurant.taxPercentage).trim()) || 5
                    : 5
            };
            const response = await axios.post(API_ENDPOINTS.RESTAURANTS_REGISTER, dataToSave);
            onRegister(response.data);
            setRestaurant({
                logo: '',
                name: '',
                owner: '',
                GST: '',
                phone: '',
                email: '',
                password: '',
                taxPercentage: '',
            });
            onClose();
        } catch (error) {
            setError(error.response ? error.response.data.message : 'An error occurred');
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
            <div className="bg-white/95 backdrop-blur-sm p-8 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-white/20">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="text-6xl mb-4">🏪</div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                        Register New Restaurant
                    </h2>
                    <p className="text-gray-600">Add a new restaurant to your platform</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-4 rounded-2xl mb-6 flex items-center gap-3">
                        <span className="text-xl">⚠️</span>
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Restaurant Info Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>🏢</span>
                            Restaurant Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="name"
                                placeholder="Restaurant Name"
                                value={restaurant.name}
                                onChange={handleChange}
                                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="text"
                                name="owner"
                                placeholder="Owner Name"
                                value={restaurant.owner}
                                onChange={handleChange}
                                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                        </div>
                        <input
                            type="text"
                            name="logo"
                            placeholder="Restaurant Logo URL"
                            value={restaurant.logo}
                            onChange={handleChange}
                            className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 mt-4"
                            required
                        />
                    </div>

                    {/* Contact Info Section */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>📞</span>
                            Contact Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="phone"
                                placeholder="Phone Number"
                                value={restaurant.phone}
                                onChange={handleChange}
                                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Email Address"
                                value={restaurant.email}
                                onChange={handleChange}
                                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                        </div>
                    </div>

                    {/* Business Info Section */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>💼</span>
                            Business Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="GST"
                                placeholder="GST Number"
                                value={restaurant.GST}
                                onChange={handleChange}
                                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="text"
                                inputMode="decimal"
                                name="taxPercentage"
                                placeholder="Tax Percentage"
                                value={restaurant.taxPercentage !== undefined && restaurant.taxPercentage !== null ? String(restaurant.taxPercentage) : '5'}
                                onChange={handleChange}
                                onBlur={(e) => {
                                    // Format the value on blur - ensure it's a valid number
                                    const value = e.target.value.trim();
                                    if (value !== '') {
                                        const numValue = Number(value);
                                        if (!isNaN(numValue)) {
                                            // Clamp value between 0 and 100
                                            const clampedValue = Math.max(0, Math.min(100, numValue));
                                            // Preserve decimal places if user entered them
                                            const decimalPlaces = value.includes('.') ? value.split('.')[1]?.length || 0 : 0;
                                            const formattedValue = decimalPlaces > 0 
                                                ? clampedValue.toFixed(Math.min(decimalPlaces, 2))
                                                : String(clampedValue);
                                            setRestaurant(prev => ({
                                                ...prev,
                                                taxPercentage: formattedValue
                                            }));
                                        }
                                    }
                                }}
                                pattern="[0-9]*\.?[0-9]*"
                                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                        </div>
                    </div>

                    {/* Security Section */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>🔒</span>
                            Security
                        </h3>
                        <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={restaurant.password}
                            onChange={handleChange}
                            className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                            required
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-4">
                        <button
                            type="submit"
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            <span>✓</span>
                            Register Restaurant
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-6 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            <span>✕</span>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegisterRestaurantModal;
